/**
 * Öffentliche KI-Chat-Seite (KEIN Login) — Route /chat. Führt einen Anfrage-Funnel:
 * beantwortet Fragen aus der Wissensbasis (RAG) und nimmt die Anfrage auf. Same-origin-Fetch
 * auf POST /api/public/chat (Tenant aus der Subdomain). Erste Assistenten-Begrüßung nur in der UI.
 *
 * Backlog #41: optionaler Bild-Anhang (Referenzfoto). Upload läuft SOFORT beim Auswählen gegen
 * POST /api/public/chat/upload (R2 "staging/") - die KI selbst sieht/braucht den Anhang nicht,
 * er ist rein fürs Team später am entstehenden Vorgang sichtbar. Die zurückgelieferte `uploadId`
 * wird nur clientseitig gehalten und mit JEDER weiteren Chat-Nachricht mitgeschickt, bis eine
 * Anfrage entsteht (server-seitiges "Claimen" - siehe public-chat.ts) - danach geleert.
 */
import { useEffect, useRef, useState } from "react";

interface Msg {
  role: "user" | "assistant";
  content: string;
}

const GREETING = "Hallo! Ich helfe Ihnen gern bei Ihrer Event- oder Catering-Anfrage. Worum geht es – Anlass, Datum, Personenzahl?";
const MAX_ATTACHMENTS = 5;

/** Datei -> {mediaType, dataBase64} (ohne data:-Präfix) fürs JSON-Body-Upload. */
async function fileToBase64(file: File): Promise<{ mediaType: string; dataBase64: string }> {
  const buf = await file.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return { mediaType: file.type || "application/octet-stream", dataBase64: btoa(binary) };
}

export default function PublicChatPage() {
  const [messages, setMessages] = useState<Msg[]>([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  async function pickAttachment(file: File) {
    if (attachmentIds.length >= MAX_ATTACHMENTS) return;
    setAttachmentBusy(true);
    setAttachmentError(null);
    try {
      const { mediaType, dataBase64 } = await fileToBase64(file);
      const res = await fetch("/api/public/chat/upload", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mediaType, dataBase64 }),
      });
      const body = (await res.json().catch(() => null)) as { data?: { uploadId: string }; message?: string } | null;
      if (res.ok && body?.data?.uploadId) {
        setAttachmentIds((ids) => [...ids, body.data!.uploadId]);
      } else {
        setAttachmentError(body?.message ?? "Anhang konnte nicht hochgeladen werden.");
      }
    } catch {
      setAttachmentError("Anhang konnte nicht hochgeladen werden.");
    } finally {
      setAttachmentBusy(false);
    }
  }

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    const next: Msg[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      // Verlauf ab der ersten Nutzer-Nachricht (die KI-API erwartet zuerst eine user-Rolle).
      const firstUser = next.findIndex((m) => m.role === "user");
      const convo = next.slice(firstUser < 0 ? 0 : firstUser).map((m) => ({ role: m.role, content: m.content }));
      const res = await fetch("/api/public/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: convo,
          website: honeypot,
          attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
        }),
      });
      const body = (await res.json().catch(() => null)) as { data?: { reply: string; inquiryCreated?: boolean } } | null;
      const reply = body?.data?.reply ?? "Entschuldigung, es gab ein Problem. Bitte versuchen Sie es erneut.";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
      // Anhaenge sind jetzt (falls vorhanden) auf die Anfrage geclaimt - clientseitig leeren,
      // sie muessen nicht laenger mitgeschickt werden.
      if (body?.data?.inquiryCreated) {
        setDone(true);
        setAttachmentIds([]);
      }
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Entschuldigung, es gab ein Verbindungsproblem. Bitte versuchen Sie es erneut." }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-2xl flex-col bg-background px-4 py-6">
      <header className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary font-serif text-xl font-bold text-on-primary">M</span>
        <div>
          <h1 className="font-display-lg text-lg font-bold text-on-background">Anfrage-Assistent</h1>
          <p className="font-body-sm text-body-sm text-secondary">Stellen Sie Ihre Fragen – wir nehmen Ihre Anfrage direkt auf.</p>
        </div>
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto rounded-2xl border border-outline-variant/40 bg-surface-container-lowest p-4">
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div
              className={
                m.role === "user"
                  ? "max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 font-body-md text-body-md text-on-primary"
                  : "max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-surface-container-high px-4 py-2.5 font-body-md text-body-md text-on-surface"
              }
            >
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm bg-surface-container-high px-4 py-2.5 font-body-md text-body-md text-secondary">…</div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {done && (
        /* CX-Audit 2026-07-17: expliziter Abschluss-Hinweis — vorher blieb offen, ob noch
           etwas zu tun ist. Weitere Nachrichten bleiben möglich (Eingabefeld bleibt aktiv). */
        <p className="mt-3 rounded-xl bg-tertiary-container px-4 py-2.5 text-center font-body-sm text-body-sm text-tertiary">
          ✓ Ihre Anfrage ist bei uns eingegangen – wir melden uns in Kürze.
          <span className="mt-0.5 block opacity-80">
            Sie können dieses Fenster jetzt schließen oder unten weitere Fragen stellen.
          </span>
        </p>
      )}

      {attachmentIds.length > 0 && (
        <p className="mt-2 font-body-sm text-body-sm text-secondary">
          📎 {attachmentIds.length} Bild{attachmentIds.length > 1 ? "er" : ""} angehängt
        </p>
      )}
      {attachmentError && <p className="mt-2 font-body-sm text-body-sm text-red-600">{attachmentError}</p>}

      <form
        className="mt-3 flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        {/* Honeypot (für Menschen unsichtbar). */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          className="hidden"
          aria-hidden="true"
        />
        {/* Backlog #41: Bild-Anhang (optional) - Upload läuft sofort beim Auswählen. */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) void pickAttachment(file);
          }}
        />
        <button
          type="button"
          className="btn-secondary h-12 shrink-0 !px-3"
          title="Bild anhängen"
          disabled={attachmentBusy || attachmentIds.length >= MAX_ATTACHMENTS}
          onClick={() => fileInputRef.current?.click()}
        >
          {attachmentBusy ? "…" : "📎"}
        </button>
        <textarea
          className="input min-h-[48px] flex-1 resize-none"
          rows={1}
          placeholder="Ihre Nachricht …"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <button type="submit" className="btn-primary h-12 shrink-0" disabled={busy || !input.trim()}>
          Senden
        </button>
      </form>
    </div>
  );
}
